import { defaultTo } from 'lodash';
import { Layout, StatusFeedback, Text } from '../../components/core';
import { PageContent, PageError, PageFooter, PageHeader, PageLoading } from '../../components/page';
import {
  useIsConnectNowServiceAvailableQuery,
  useOrganizationConnectNowQuery,
} from '../../graphQL';
import { getRoute } from '../../routes';
import { useTitle } from '../../utils/useTitle';
import { ContactMethods } from './components/ContactMethods';
import { Offerings } from './components/Offerings';
import { useRedirectFromOnDemand } from './hooks/useRedirectFromOnDemand';

export const ConnectNowLandingPage = (): JSX.Element => {
  useTitle('ConnectNow');

  useRedirectFromOnDemand();

  const { data: connectNowData, loading, error, refetch } = useOrganizationConnectNowQuery();
  const connectNowEnabled = defaultTo(
    connectNowData?.organizationConnectNow?.connectNowEnabled,
    false,
  );
  const { data: connectNowServiceAvailability, error: connectNowServiceAvailabilityError } =
    useIsConnectNowServiceAvailableQuery();

  const isDuringServiceHours = connectNowServiceAvailability?.isConnectNowServiceAvailable ?? false;

  if (connectNowServiceAvailabilityError) {
    return <PageError goBackTo="connectNowHome" />;
  }

  if (loading) {
    return <PageLoading pageName="ConnectNow landing page" />;
  }

  if (!connectNowEnabled) {
    return <PageError goBackTo={getRoute('home', {})} />;
  }

  if (error) {
    return <PageError showContact onRefreshPress={refetch} />;
  }

  const connectNowCrisisContact = connectNowData?.organizationConnectNow?.connectNowData;
  const interpretationServicesSMS =
    connectNowData?.organizationConnectNow?.connectNowInterpretationServicesData
      ?.crisisInterpretationSMS ?? undefined;

  return (
    <PageContent testID="connect-now-landing-page">
      <Layout.VStack space={4}>
        <PageHeader
          title="ConnectNow"
          undertext="Talk with a mental health staff member who is trained to support you in a high-stress situation with sensitivity and expertise."
        />
        <Offerings />
        {connectNowCrisisContact && (
          <ContactMethods
            connectNowData={connectNowCrisisContact}
            isDuringServiceHours={isDuringServiceHours}
            interpretationServicesSMS={interpretationServicesSMS}
          />
        )}
        <StatusFeedback.danger testID="connect-now-emergency-warning">
          <Text.paraLarge>
            If you’re in an emergency or crisis and need immediate help, call 911 or go to the
            nearest emergency room.
          </Text.paraLarge>
        </StatusFeedback.danger>
        <PageFooter />
      </Layout.VStack>
    </PageContent>
  );
};
